// @flow
import BaseComponent from '../base_component/base_component'
import componentLocator from '../../assets/javascripts/component_locator'
import kebabCase from 'lodash-es/kebabCase'

export default class Modal extends BaseComponent {
    currentComponent: ?string;
    $modal: JQuery;
    open: Boolean;
    $panels: JQuery;
    persist: Array<string>;
    locked: boolean;
    queue: Array<Function>;

    constructor(id: number, component_name: string, options: any) {
        super(id, component_name, options);
        this.currentComponent = null;
        this.queue = [];

        this.persist = [].concat(this.options.persist || []);
        this.persist.forEach(panel => {
            $(`#wrapper_${this.options[panel]}`).hide();
        });
        this.$modal = $(this.component).find('.modal');
        this.$panels = $(this.component).find('.modal-content > .wrapper');
        $(document.body).on('keydown', event => this._trapTabInput(event));
        this.addCloseHandler();
        this.addImplicitSupport();
    }

    addCloseHandler() {
        let self = this;
        let mouseOffContent = false;
        this.$modal.mousedown(event => {
            mouseOffContent = !$(this.component).has(event.target).length;
        });
        this.$modal.mouseup(() => {
            if ($(this.component).find('.modal-content:hover').length === 0 &&
                    mouseOffContent) {
                this.close();
            }
            mouseOffContent = false;
        });
        $(this.component).find('.close-modal').click(() => { this.close(); });
    }

    addImplicitSupport() { // Because bootstrap does.
        let self = this;
        $(document).on('click', '[data-target="#modal"]', (e: JQueryEventObject) => {
            let remote = $(e.target).closest('[data-target]').attr('data-remote');
            let [_, path, args] = remote.match(/\/(.*)\?(.*)/) || [null, null];
            if (path === null || args === null) return;

            let argHash = args.split('&').reduce((hash, arg) => {
                let [key, value] = arg.split('=');
                hash[key] = value;
                return hash;
            }, {});
            self.show(path, argHash);
        });

        let paramsArray: any = location.search.match(/([?\&])([^=]*)=[^$\&#]*/g);
        if (!paramsArray) return;

        let params: Map<string, string> = new Map(paramsArray.map(v => v.substr(1).split('=')));
        if (!params.has('modal')) return;
        
        componentLocator.waitUntilLoaded().then(() => {
            this.show(params.get('modal') || '', Object.fromEntries(params));
        });
    }

    changeModalSize(ref: string) {
        if (ref) {
            $("#modal-id").addClass("document-preview-modal-lg");
        } else {
            $("#modal-id").removeClass("document-preview-modal-lg");
        }
    }

    show(panelName: string, extraParams: {[string]: any}={}) {
        this.changeModalSize(extraParams['ref']);
        let closedAComponent = this._closeExisting();

        if (this._acquireLock() === false) {
            this.queue.push(() => this.show(panelName, extraParams));
            return;
        }

        let componentId = this._getComponentId(panelName);
        this.currentComponent = panelName;

        if (this.persist.includes(panelName)) {
            $(`#wrapper_${componentId}`).show();
        } else {
            this._renderComponent(componentId, extraParams);
        }

        this.$modal.addClass(`${kebabCase(panelName.replace(' ', '-'))}-modal`);
        this._mountModal(closedAComponent);
    }

    _trapTabInput(event: JQueryEvent, filterIndex=true) {
        if (!this.open || event.which != 9) return;

        let items = $(this.component).find('a, button, input, textarea, select, details');
        if (filterIndex) {
            items = items.filter(':not([tabindex="-1"])').toArray();
        }
        let focussed = $(this.component).find(':focus')[0];
        let focusIndex = items.indexOf(focussed);
        let nextItem = null;
        if (event.shiftKey) {
            focusIndex = Math.max(focusIndex, 0); // -1 means 'not selected' - reset this to 0.
            nextItem = items[(focusIndex + items.length - 1) % items.length];
        } else {
            nextItem = items[(focusIndex + 1) % items.length];
        }
        if (nextItem) nextItem.focus();
        return false;
    }

    _closeExisting(): boolean {
        if (this.currentComponent === null) return false;

        this.close(false);
        return true;
    }

    _getComponentId(panelName: string): number {
        if (!this.options[panelName]) {
            throw `Could not find a modal panel named ${panelName}`;
        }
        return this.options[panelName];
    }

    _renderComponent(componentId: number, extraParams: {[string]: any}) {
        Object.keys(extraParams).forEach(param => {
            window.__componentAdapter.setExtraParam(
                componentId, param, extraParams[param]);
        });

        // Render the component, once, then call getReady
        let setReady = false;
        window.__componentAdapter.render(componentId, false, () => {
            if (setReady) return;
            setReady = true;
            componentLocator.getById(componentId).then(c => (c ? c.onReady() : null));
        });
    }
    
    close(closeModal: boolean=true) {
        if (this._acquireLock() === false) {
            this.queue.push(() => this.close(closeModal));
            return;
        }

        let currentComponent = this.currentComponent;
        this.$modal.removeClass(`${kebabCase(currentComponent.replace(' ', '-'))}-modal`);
        if (closeModal && this.$modal.hasClass('in')) {
            this.$modal.modal('hide');
            this._handleEvent('hidden.bs.modal', () => {
                this.open = false;
                this._dismountModal(currentComponent);
            });
        } else {
            this._dismountModal(currentComponent);
        }
        this.currentComponent = null;
    }

    _handleEvent(eventName: string, callback: Function) {
        this.$modal.off(eventName);
        this.$modal.on(eventName, callback);
    }

    _mountModal(closedAComponent: boolean) {
        this.$modal.modal('show');
        if (closedAComponent) {
            this._releaseLock()
        } else {
            this._handleEvent('shown.bs.modal', () => {
                this.open = true;
                this._releaseLock()
            });
        }
    }

    _dismountModal(currentComponent: ?string) {
        let componentId = this.options[currentComponent];
        let childComponent = window.getComponent(componentId);

        if (this.persist.includes(currentComponent)) {
            $(`#wrapper_${componentId}`).hide();
        } else if (childComponent) {
            childComponent.dismount();
        } else {
            $(`#wrapper_${componentId}`).html('');
        }
        this._releaseLock();
    }

    _acquireLock(): boolean {
        if (!this.locked) {
            this.locked = true;
            return true;
        } else {
            return false;
        }
    }

    _releaseLock() {
        this.locked = false;
        if (this.queue.length > 0) {
            this.queue.pop()();
        }
    }
}